import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {message} from 'antd';
import {createStructuredSelector} from 'reselect';

import {
    NOTIFICATIONS_GET,
    NOTIFICATIONS_MARK_ALL_AS_READ,
    NOTIFICATIONS_MARK_AS_READ,
} from '../../consts/api/apiMethods';
import ApiClient from '../../services/api/clients/apiClient';

const initialState = {
    notifications: [],
    error: null,
};

export const fetchNotifications = createAsyncThunk(
    'notifications/fetchNotifications',
    async (onlyUnread = true, {rejectWithValue}) => {
        try {
            return await new ApiClient().callGet({
                methodName: NOTIFICATIONS_GET,
                skipLoader: true,
                requestConfig: {
                    params: {onlyUnread},
                },
            });
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const makeNotificationsSelector = () =>
    createStructuredSelector({
        notifications: (state) => state.notifications.notifications,
        error: (state) => state.notifications.error,
        unreadCount: (state) => state.notifications.notifications.filter((n) => !n.isRead).length,
    });

export const handleMarkAsRead =
    (id, feedback = message) =>
    async (dispatch) => {
        dispatch(markAsReadLocal(id));

        try {
            await new ApiClient().callPut({
                methodName: NOTIFICATIONS_MARK_AS_READ,
                skipLoader: true,
                requestConfig: {
                    args: id,
                },
            });
            feedback.success('Сповіщення прочитано');
        } catch (e) {
            console.error('API Error:', e);
            feedback.error('Помилка при оновленні сповіщення');
            dispatch(fetchNotifications());
        }
    };

export const onReadAllClick =
    (feedback = message) =>
    async (dispatch) => {
        dispatch(markAllAsReadLocal());

        try {
            await new ApiClient().callPut({
                methodName: NOTIFICATIONS_MARK_ALL_AS_READ,
                skipLoader: true,
            });
            feedback.success('Всі сповіщення прочитано');
        } catch (e) {
            console.error('API Error:', e);
            feedback.error('Помилка при оновленні сповіщень');
            dispatch(fetchNotifications());
        }
    };

export const handleIncomingNotification = (notification) => (dispatch) => {
    dispatch(receiveNewNotification(notification));

    const notificationCode = notification.code || notification.typeCode || notification.type;

    window.dispatchEvent(
        new CustomEvent('onSystemNotification', {
            detail: {type: notificationCode},
        })
    );
};

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        receiveNewNotification: (state, action) => {
            state.notifications.unshift(action.payload);
        },
        markAsReadLocal: (state, action) => {
            state.notifications = state.notifications.filter((n) => n.id !== action.payload);
        },
        markAllAsReadLocal: (state) => {
            state.notifications = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                const data = Array.isArray(action.payload) ? action.payload : [];

                state.notifications = [...data].sort((a, b) => {
                    const dateA = new Date(a.createdAt);
                    const dateB = new Date(b.createdAt);

                    return dateB - dateA;
                });
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.error = action.payload || 'Unknown error';
            });
    },
});

export const {receiveNewNotification, markAsReadLocal, markAllAsReadLocal} = notificationsSlice.actions;

export default notificationsSlice.reducer;
